import styled from '@emotion/styled';
import { H3, Text, H2 } from 'ds/components/typography';
import { typographyFontSizeCfg, typographyLineHeightCfg, weightStringToNumber } from 'ds/components/typography/types';
import { HEADER_HEIGHT } from 'ds/components/Layout';
import { DESKTOP_NAVIGATION_HEIGHT } from 'screens/DeveloperPage/components/navigation/styled';
import { COLLAPSED_CARD_HEIGHT, EXPANDED_CARD_HEIGHT } from 'screens/DeveloperPage/components/business-card/styled';
import { colorToAlpha } from 'helpers/insight';
import { Breadcrumbs } from 'components/footer/styled';

const CONTENT_MAX_WIDTH = 1144;

export const Wrapper = styled.div`
  ${({ theme }) => `
    color: ${theme.colors.neutrals.grey1};
  `}
`;

export const MapWrapper = styled.div<{basicIntegration: boolean}>`
  ${({ theme, basicIntegration }) => theme.media.showAt({ from: 3 })`
     ${basicIntegration && `
      background: linear-gradient(180deg, ${theme.colors.neutrals.grey10} 0, ${colorToAlpha(theme.colors.neutrals.grey10, 0)} 125px);
      padding-top: ${theme.spacing(7)};
    `};
  `}
`;

export const ProjectsWrapper = styled.div<{ noMargin: boolean; }>`
  ${({ theme, noMargin }) => theme.media.showAt({ from: 3 })`
    ${noMargin ? '' : `margin-top: ${theme.spacing(7)};`}
  `}
  ${({ theme }) => theme.media.showAt({ to: 2 })`
    margin-top: ${theme.spacing(5)};
  `}
`;

export const SectionWrapper = styled.div<{noTopMargin?: boolean; }>`
  ${({ theme, noTopMargin }) => theme.media.showAt({ from: 3 })`
    max-width: ${CONTENT_MAX_WIDTH}px;
    margin: ${noTopMargin ? 0 : theme.spacing(12)} auto 0;
  `}
`;

export const SubsectionWrapper = styled.div<{ withBorder?: boolean; noTopMargin?: boolean; }>`
  ${({ theme, noTopMargin }) => theme.media.showAt({ from: 3 })`
    ${noTopMargin ? '' : `margin-top: ${theme.spacing(8)};`}
  `}
  ${({ theme }) => theme.media.showAt({ from: 2, to: 3 })`
    margin-left: ${theme.spacing(3)};
    margin-right: ${theme.spacing(3)};
  `}

  ${({ theme, withBorder, noTopMargin }) => theme.media.showAt({ to: 2 })`
    margin: ${noTopMargin ? 0 : theme.spacing(3)} ${theme.spacing(3)} 0;
    ${withBorder ? `
      border: 1px solid ${theme.colors.neutrals.grey8};
      border-radius: ${theme.borderRadius.small};
      padding: ${theme.spacing(2)};
    ` : ''}
  `}
`;

export const SectionTitle = styled(H3.withComponent('h2'))`
  font-weight: ${weightStringToNumber.bold};

  ${({ theme }) => theme.media.showAt({ to: 2 })`
    margin-bottom: ${theme.spacing(2.5)};
  `}

  ${({ theme }) => theme.media.showAt({ from: 3 })`
    font-size: ${typographyFontSizeCfg.h2.mobile}px;
    line-height: ${typographyLineHeightCfg.h2.mobile}px;
    margin-bottom: ${theme.spacing(3)};
  `}
`;

export const StickySection = styled.div`
  ${({ theme }) => theme.media.showAt({ from: 2 })`
    position: fixed;
    width: 100%;
    top: ${HEADER_HEIGHT}px;
    left: 0;
    z-index: 3;
  `}
`;

export const ContentRoot = styled.div`
  ${({ theme }) => `
    margin-top: ${theme.spacing(7)};
  `}
`;

export const FooterWrapper = styled.div<{ hasContact: boolean; }>`
  ${({ theme, hasContact }) => theme.media.showAt({ at: 1 })`
    margin-top: ${theme.spacing(10)};
    ${hasContact ? `margin-bottom: ${theme.spacing(12)};` : ''}
  `}
  ${({ theme }) => theme.media.showAt({ from: 2 })`margin-top: ${theme.spacing(5)};`}
`;

export const COLLAPSED_STICKY_HEIGHT = DESKTOP_NAVIGATION_HEIGHT + COLLAPSED_CARD_HEIGHT;
export const EXPANDED_STICKY_HEIGHT = DESKTOP_NAVIGATION_HEIGHT + EXPANDED_CARD_HEIGHT;

export const EmptySpacingBlock = styled.div<{ collapsed: boolean; }>`
  ${({ collapsed }) => `
    height: ${EXPANDED_STICKY_HEIGHT}px;
  `}
`;

export const SectionTitleWrapper = styled.div<{ isPromoted?: boolean }>`
  ${({ theme }) => `
    background: linear-gradient(180deg, ${theme.colors.neutrals.grey10} 0, ${colorToAlpha(theme.colors.neutrals.grey10, 0)} 125px);
  `}
  ${({ theme, isPromoted }) => theme.media.showAt({ at: 1 })`
    padding: ${theme.spacing(4)} 0 ${isPromoted ? theme.spacing(3) : 0};
    background: linear-gradient(180deg, ${theme.colors.neutrals.grey10} 0, ${colorToAlpha(theme.colors.neutrals.grey10, 0)} 100%);
  `}
  ${({ theme, isPromoted }) => theme.media.showAt({ from: 2 })`
    padding: ${theme.spacing(7)} 0 ${theme.spacing(isPromoted ? 3 : 4)};
  `}
`;

export const MainTitleWrapper = styled.div`
  max-width: ${CONTENT_MAX_WIDTH}px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  ${({ theme }) => `
    > svg {
      margin-left: ${theme.spacing(2)};
      filter: drop-shadow(0px 1px 10px rgba(0, 0, 0, 0.08));
    }
  `}
  ${({ theme }) => theme.media.showAt({ at: 1 })`
    > svg {
      margin-left: ${theme.spacing(1)};
    }
  `}
  ${({ theme }) => theme.media.showAt({ from: 1, to: 3 })`
    padding: 0 ${theme.spacing(3)};
  `}
`;

export const MainSectionTitle = styled(H2)`
  ${({ theme }) => `
    margin-bottom: ${theme.spacing(0.5)};
  `}
  ${({ theme }) => theme.media.showAt({ at: 1 })`
    font-size: ${typographyFontSizeCfg.h3.mobile}px;
  `}
  ${({ theme }) => theme.media.showAt({ from: 2 })`
    font-size: ${typographyFontSizeCfg.h2.mobile}px;
  `}
`;

export const MainSectionSubtitle = styled(Text)``;

export const BlogSectionHeader = styled(SectionTitle)`
  ${({ theme }) => `
    &&& {
      margin-bottom: ${theme.spacing(2)};
    }
  `}
`;

export const BlogSectionWrapper = styled.div`
  margin: 0 auto;
  > div {
    padding-top: 0;
    padding-bottom: 0;
  }
  ${({ theme }) => theme.media.showAt({ at: 1 })`padding: 0 ${theme.spacing(2)};`};
  ${({ theme }) => theme.media.showAt({ at: 2 })`padding: 0 ${theme.spacing(5)};`};
  ${({ theme }) => theme.media.showAt({ at: 3 })`max-width: 864px;`};
  ${({ theme }) => theme.media.showAt({ from: 4 })`
    max-width: 1144px;
    margin-bottom: -${theme.spacing(12)};
  `};
  ${({ theme }) => theme.media.showAt({ to: 2 })`
    margin-top: ${theme.spacing(6)};
  `}

  ${({ theme }) => theme.media.showAt({ from: 3 })`
    margin-top: ${theme.spacing(9)};
    margin-bottom: ${theme.spacing(9)};
  `}
`;

export const BreadcrumbsSection = styled(Breadcrumbs)`
  margin-bottom: 0;
  padding-top: ${({ theme }) => theme.spacing(5)};
  border-top: 1px solid ${({ theme }) => theme.colors.neutrals.grey7};
`;
